import { useState } from 'react';
import { AlertCircle, Activity, Bug, Radio } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Sentry, isSentryEnabled } from '@/sentry';
import { AppError, handleError } from '@/utils/errorHandler';

export default function SentryTestPage() {
  const [shouldCrash, setShouldCrash] = useState(false);
  const [lastAction, setLastAction] = useState<string | null>(null);

  if (shouldCrash) {
    throw new Error('Sentry test render error');
  }

  const sendHandledError = () => {
    handleError(
      new AppError('Sentry handled test error', 'SENTRY_TEST_ERROR'),
      {
        component: 'SentryTestPage',
        action: 'sendHandledError',
      }
    );
    setLastAction('Handled error sent via the shared error handler.');
  };

  const sendLogAndMetric = () => {
    Sentry.logger.info('Sentry test page log event', {
      action: 'sendLogAndMetric',
      page: 'admin/sentry-test',
    });

    Sentry.metrics.count('sentry.test.page.click', 1, {
      attributes: {
        action: 'sendLogAndMetric',
        page: 'admin/sentry-test',
      },
    });

    setLastAction('Test log and metric sent to Sentry.');
  };

  const sendMessage = () => {
    Sentry.captureMessage('Sentry test page message capture', 'info');
    setLastAction('Message event sent to Sentry.');
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Sentry Test Page</h1>
          <p className="text-sm text-muted-foreground">
            Use this page to verify error monitoring, tracing, logs, metrics, and session replay.
          </p>
        </div>
        <Badge variant={isSentryEnabled ? 'default' : 'secondary'}>
          {isSentryEnabled ? 'Sentry configured' : 'Sentry disabled'}
        </Badge>
      </div>

      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Safe verification route</AlertTitle>
        <AlertDescription>
          This page is behind the admin login route and lets you intentionally emit test events without
          affecting normal users.
        </AlertDescription>
      </Alert>

      <div className="grid gap-6 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bug className="h-5 w-5" />
              Error Monitoring
            </CardTitle>
            <CardDescription>
              Send a handled error through the shared app error pipeline.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button className="w-full" onClick={sendHandledError}>
              Send handled error
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Radio className="h-5 w-5" />
              Logs and Metrics
            </CardTitle>
            <CardDescription>
              Emit a structured log line and counter metric from the browser.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button className="w-full" onClick={sendLogAndMetric} variant="secondary">
              Send log and metric
            </Button>
            <Button className="w-full" onClick={sendMessage} variant="outline">
              Send message event
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              Replay and Tracing
            </CardTitle>
            <CardDescription>
              Navigating to this page creates a route trace. Trigger a render crash to verify replay on error.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button className="w-full" onClick={() => setShouldCrash(true)} variant="destructive">
              Trigger render crash
            </Button>
          </CardContent>
        </Card>
      </div>

      {lastAction ? (
        <Card>
          <CardHeader>
            <CardTitle>Last Action</CardTitle>
            <CardDescription>Most recent test event sent from this page.</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">{lastAction}</p>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
